import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Download, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { Spinner } from "@/components/ui/Spinner";
import { getParcelKwMergedPdf } from "../api";
import { useKwMutations, useParcelKwFiles, useTaskPolling } from "../hooks";
import { fmtDateTime } from "../utils";
import { saveBlobResponse } from "@/utils/download";
import type { KwOwnerDiff, ParcelKwFile } from "../types";

// Wgraj odpis KW (pobrany z EKW) → AI czyta Dział II (właściciele), III (służebności/
// ograniczenia), IV (hipoteki) + ostrzeżenia. Zatwierdzeni właściciele lądują na działce.
// Działający adres wyszukiwarki EKW (z parametrami — bez nich portal potrafi odrzucić
// wejście). POST-em prefillować się NIE da (psuje stronę), więc otwieramy zwykłym GET,
// a numer KW pokazujemy na przycisku do skopiowania jednym kliknięciem.
// Działy księgi tak, jak pokazuje je wydruk EKW (zakładki nad treścią).
const KW_SECTIONS = [
  { key: "okladka", label: "Okładka", hint: "nr księgi, sąd, typ" },
  { key: "dio", label: "Dział I-O", hint: "oznaczenie nieruchomości (działki, obręb, pow.)" },
  { key: "disp", label: "Dział I-Sp", hint: "prawa związane z własnością" },
  { key: "dii", label: "Dział II", hint: "WŁAŚCICIELE / użytkownicy wieczyści + udziały" },
  { key: "diii", label: "Dział III", hint: "prawa, roszczenia, ograniczenia, służebności" },
  { key: "div", label: "Dział IV", hint: "hipoteki" },
];

/** Ostrzeżenia bywają zapisane źle: jako string albo — w starszych rekordach — jako
 *  tablica POJEDYNCZYCH ZNAKÓW (model oddał tekst, a backend go rozbił). Sklejamy
 *  je z powrotem i, gdy to JSON, rozpakowujemy do listy. */
function kwWarnings(value: unknown): string[] {
  if (!value) return [];
  if (typeof value === "string") return [value];
  if (!Array.isArray(value)) return [];
  const items = value.map((v) => String(v));
  const looksSplit = items.length > 3 && items.every((s) => s.length <= 1);
  if (!looksSplit) return items.filter((s) => s.trim());
  const joined = items.join("");
  try {
    const parsed = JSON.parse(joined);
    if (Array.isArray(parsed)) return parsed.map(String).filter((s) => s.trim());
  } catch {
    /* nie JSON — pokaż jako jeden tekst */
  }
  return joined.trim() ? [joined.trim()] : [];
}

const EKW_SEARCH_URL =
  "https://przegladarka-ekw.ms.gov.pl/eukw_prz/KsiegiWieczyste/wyszukiwanieKW" +
  "?komunikaty=true&kontakt=true&okienkoSerwisowe=false";

export function KwAnalysisModal({
  parcel,
  onClose,
}: {
  parcel: { id: string; teryt: string; kw_number?: string };
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data: files, isLoading } = useParcelKwFiles(parcel.id);
  const m = useKwMutations(parcel.id);
  const inputRef = useRef<HTMLInputElement>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const task = useTaskPolling(taskId);
  const running =
    m.upload.isPending ||
    m.analyze.isPending ||
    (!!taskId && !["success", "failure", "cancelled"].includes(task.data?.status ?? ""));

  // Zadanie analizy skończone → odśwież listę (wynik zapisał się na pliku KW).
  useEffect(() => {
    const s = task.data?.status;
    if (taskId && s && ["success", "failure", "cancelled"].includes(s)) {
      qc.invalidateQueries({ queryKey: ["pp", "parcel-kw", parcel.id] });
      setTaskId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.data?.status]);

  // Otwiera EKW w nowej karcie. Portal odrzuca wejścia z Refererem ELGW5 („Access
  // Denied”) i blokuje cross-site POST (Sec-Fetch-Site), więc startujemy z about:blank
  // + no-referrer i robimy zwykłą nawigację — dla portalu wygląda to jak ręcznie
  // wklejony adres. Treści obcej domeny przeglądarka odczytać nie pozwala
  // (Same-Origin Policy), dlatego wraca ona przez wklejenie przez użytkownika.
  const openEkw = () => {
    if (parcel.kw_number) navigator.clipboard?.writeText(parcel.kw_number).catch(() => undefined);
    const w = window.open("", "_blank");
    if (!w) return;
    // Start z about:blank + no-referrer, potem location.replace — dla portalu wygląda
    // to jak ręczne wklejenie adresu (żadnego Referera, żadnego cross-site POST-a).
    w.document.write(
      '<!doctype html><meta name="referrer" content="no-referrer">' +
        '<title>EKW…</title><body style="font:14px system-ui;padding:16px">Otwieram EKW…' +
        `<script>location.replace(${JSON.stringify(EKW_SEARCH_URL)})<\/script>`,
    );
    w.document.close();
  };

  // Wtyczka „ELGW5 KW Helper" (przeglądarka biura) — jeśli jest zainstalowana, można
  // zlecić pobranie tej jednej księgi bez ręcznego wklejania: wtyczka wchodzi na EKW
  // w tej przeglądarce, przepisuje kod z obrazka i odsyła treść. Backend nigdy nie
  // łączy się z EKW (portal odbija serwerownię i tryb bezgłowy).
  const [helper, setHelper] = useState(
    () => document.documentElement.getAttribute("data-elgw5-ekw-helper") === "1",
  );
  const [helperState, setHelperState] = useState<"idle" | "running" | "done" | "error">("idle");
  useEffect(() => {
    const onMsg = (ev: MessageEvent) => {
      if (ev.source !== window) return;
      if (ev.data?.type === "ELGW5_KW_HELPER_READY") setHelper(true);
      if (ev.data?.type === "ELGW5_FETCH_KW_ACK" && !ev.data.ok) setHelperState("error");
      if (ev.data?.type === "ELGW5_KW_DONE") {
        setHelperState(ev.data.ok ? "done" : "error");
        qc.invalidateQueries({ queryKey: ["pp", "parcel-kw", parcel.id] });
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [qc, parcel.id]);

  const fetchViaHelper = () => {
    setHelperState("running");
    window.postMessage(
      { type: "ELGW5_FETCH_KW", kwNumber: parcel.kw_number, parcelId: parcel.id },
      "*",
    );
  };

  // Wklejona treść „przeglądania KW" → analiza AI (bez plików, bez instalacji).
  // EKW pokazuje księgę działami (zakładki u góry wydruku), więc zbieramy je osobno
  // i sklejamy z nagłówkami — AI dostaje czytelną strukturę zamiast jednej breji.
  const [sections, setSections] = useState<Record<string, string>>({});
  const setSection = (k: string, v: string) => setSections((s) => ({ ...s, [k]: v }));
  // Jest już zapisana księga? → formularz wklejania domyślnie schowany.
  const hasSaved = (files ?? []).length > 0;
  const [showPaste, setShowPaste] = useState(false);
  const combined = KW_SECTIONS
    .filter((s) => (sections[s.key] || "").trim())
    .map((s) => `=== ${s.label} ===\n${sections[s.key].trim()}`)
    .join("\n\n");

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    m.upload.mutate(file, { onSuccess: (r) => setTaskId(r.task_id ?? null) });
    e.target.value = "";
  };

  return (
    <Modal open onClose={onClose} title={`Księga wieczysta — działka ${parcel.teryt}`}>
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <input ref={inputRef} type="file" accept="application/pdf" className="hidden" onChange={onPick} />
          <Button size="sm" onClick={() => inputRef.current?.click()} loading={m.upload.isPending} disabled={running}>
            <Upload size={14} /> Wgraj odpis KW (PDF z EKW)
          </Button>
          <Button size="sm" variant="secondary" onClick={openEkw} disabled={running}
            title="Otwiera EKW w nowej karcie z wpisanym numerem KW tej działki">
            Otwórz EKW {parcel.kw_number ? `(${parcel.kw_number})` : ""}
          </Button>
          {helper && parcel.kw_number && (
            <Button size="sm" variant="secondary" onClick={fetchViaHelper}
              disabled={running || helperState === "running"}
              title="Wtyczka wejdzie na EKW w tej przeglądarce, przepisze kod z obrazka i pobierze treść księgi">
              {helperState === "running" ? "Pobieram z EKW…" : "Pobierz automatycznie (wtyczka)"}
            </Button>
          )}
          {helperState === "done" && (
            <span className="text-xs text-emerald-600">Pobrano i przeanalizowano.</span>
          )}
          {helperState === "error" && (
            <span className="text-xs text-red-600">
              Wtyczka nie dała rady — sprawdź jej panel (zatrzymanie + zrzut ekranu).
            </span>
          )}
          {running && (
            <span className="flex items-center gap-1.5 text-xs text-[var(--color-text-secondary)]">
              <Spinner className="h-3 w-3" /> Analiza AI…
            </span>
          )}
        </div>
        {(!hasSaved || showPaste) && (
          <>
            <ol className="text-[11px] text-[var(--color-text-muted)] list-decimal pl-4 space-y-0.5">
              <li>„Otwórz EKW” — otwiera wyszukiwarkę; numer <b>{parcel.kw_number || "—"}</b> ląduje w schowku (wklej <b>Ctrl+V</b> w pole kodu wydziału).</li>
              <li>Kliknij „Wydruk zwykły” / „Przeglądanie aktualnej treści KW”.</li>
              <li>Zaznacz całość (<b>Ctrl+A</b>), skopiuj (<b>Ctrl+C</b>) i wklej niżej — albo wydrukuj do PDF i wgraj.</li>
            </ol>
            <p className="text-[11px] text-[var(--color-text-muted)]">
              AI odczyta właścicieli (Dział II), służebności/ograniczenia (III) i hipoteki (IV).
              Treść zapisuje się w bazie — <b>drugi raz na EKW wchodzić nie trzeba</b>.
            </p>
          </>
        )}

        <div className="space-y-2">
          {/* Gdy księga jest już zapisana w bazie, formularz wklejania jest ZWINIĘTY —
              rozwijasz go tylko, gdy chcesz dograć nowszy stan księgi. */}
          {hasSaved && !showPaste ? (
            <button className="text-[11px] text-[var(--color-primary)] hover:underline"
              onClick={() => setShowPaste(true)}>
              + Wklej nowszą treść z EKW (aktualizacja stanu księgi)
            </button>
          ) : (
          <>
          <p className="section-label">Wklej treść działami (Ctrl+A / Ctrl+C na każdej zakładce EKW)</p>
          {KW_SECTIONS.map((s) => (
            <label key={s.key} className="block">
              <span className="text-[11px] text-[var(--color-text-muted)]">
                <b>{s.label}</b> — {s.hint}
                {(sections[s.key] || "").trim() ? " ✓" : ""}
              </span>
              <textarea
                className="input-base w-full h-16 text-[11px] font-mono mt-0.5"
                placeholder={`Wklej „${s.label}”…`}
                value={sections[s.key] || ""}
                disabled={running || m.paste.isPending}
                onChange={(e) => setSection(s.key, e.target.value)}
              />
            </label>
          ))}
          <div className="flex items-center gap-2">
            <Button size="sm" disabled={combined.trim().length < 100 || running}
              loading={m.paste.isPending}
              onClick={() => m.paste.mutate(
                { text: combined, kwNumber: parcel.kw_number, sections },
                { onSuccess: () => { setSections({}); setShowPaste(false); } },
              )}>
              Analizuj wklejone działy
            </Button>
            <span className="text-[11px] text-[var(--color-text-muted)]">
              Minimum przydatne: <b>Dział II</b> (właściciele). III i IV dają służebności i hipoteki.
            </span>
          </div>
          {m.paste.isError && (
            <p className="text-[11px] text-[var(--color-primary)]">Nie udało się przeanalizować wklejonej treści.</p>
          )}
          </>
          )}
        </div>

        {isLoading ? (
          <div className="py-3"><Spinner className="h-4 w-4" /></div>
        ) : (files ?? []).length === 0 ? (
          <p className="text-xs text-[var(--color-text-muted)]">Brak wgranych ksiąg wieczystych dla tej działki.</p>
        ) : (
          <div className="space-y-3">
            {(files ?? []).map((f) => (
              <KwFileCard key={f.id} file={f} parcelId={parcel.id} onTask={setTaskId} disabled={running} />
            ))}
          </div>
        )}

        <div className="flex justify-end pt-1">
          <Button variant="ghost" size="sm" onClick={onClose}>Zamknij</Button>
        </div>
      </div>
    </Modal>
  );
}

/** Rozbieżności właścicieli KW ↔ działka z możliwością ich AKCEPTACJI.
 *  Typowy przypadek: ten sam podmiot zapisany inaczej („GMINA WAGANIEC" na działce,
 *  „Urząd Gminy Waganiec" w KW). Akceptacja to decyzja człowieka — nie rusza ani
 *  właścicieli działki, ani analizy; zdejmuje tylko rozbieżność z werdyktu, dzięki
 *  czemu działka świeci na liście na zielono. */
function OwnerDiffResolver({
  diff, fileId, parcelId, disabled,
}: {
  diff: KwOwnerDiff;
  fileId: string;
  parcelId: string;
  disabled: boolean;
}) {
  const m = useKwMutations(parcelId);
  // Sparowanie „nazwa z KW → nazwa z działki" wybrane przez użytkownika.
  const [pairs, setPairs] = useState<Record<string, string>>({});
  const accept = (kwName: string, dbName: string) =>
    m.acceptDiff.mutate({ id: fileId, kwName, dbName });
  const busy = disabled || m.acceptDiff.isPending;

  return (
    <div className="mt-1 border-l-2 border-amber-500 pl-2 space-y-1.5">
      <p className="text-amber-700 dark:text-amber-400 font-medium">
        ⚠ Rozbieżność z właścicielami na działce (z wypisów)
      </p>
      {diff.db_empty && (
        <p className="text-[var(--color-text-muted)]">(działka nie ma jeszcze właścicieli z wypisu)</p>
      )}
      {diff.only_in_kw.map((kwName) => (
        <div key={kwName} className="flex flex-wrap items-center gap-1.5">
          <span className="shrink-0">W KW: <b>{kwName}</b></span>
          <span className="text-[var(--color-text-muted)]">to samo co</span>
          <select
            className="input-base text-[11px] py-0.5"
            value={pairs[kwName] ?? ""}
            disabled={busy}
            onChange={(e) => setPairs((p) => ({ ...p, [kwName]: e.target.value }))}
          >
            <option value="">— nikt (brak na działce) —</option>
            {diff.only_in_db.map((dbName) => (
              <option key={dbName} value={dbName}>{dbName}</option>
            ))}
          </select>
          <button
            className="text-[var(--color-primary)] hover:underline disabled:opacity-40"
            disabled={busy}
            onClick={() => accept(kwName, pairs[kwName] ?? "")}
            title="Uznaj tę rozbieżność za wyjaśnioną — przestanie się liczyć jako różnica"
          >
            Akceptuj
          </button>
        </div>
      ))}
      {/* Nazwy z działki, których nie ma w KW i których nikt nie sparował — można je
          zaakceptować pojedynczo (np. współwłaściciel ujawniony tylko w wypisie). */}
      {diff.only_in_db
        .filter((dbName) => !Object.values(pairs).includes(dbName))
        .map((dbName) => (
          <div key={dbName} className="flex flex-wrap items-center gap-1.5">
            <span>Na działce, brak w KW: <b>{dbName}</b></span>
            <button
              className="text-[var(--color-primary)] hover:underline disabled:opacity-40"
              disabled={busy}
              onClick={() => accept("", dbName)}
              title="Uznaj za wyjaśnione"
            >
              Akceptuj
            </button>
          </div>
        ))}
    </div>
  );
}

/** Lista wyjaśnionych rozbieżności — z możliwością cofnięcia akceptacji. */
function AcceptedDiffList({
  diff, fileId, parcelId, disabled,
}: {
  diff: KwOwnerDiff;
  fileId: string;
  parcelId: string;
  disabled: boolean;
}) {
  const m = useKwMutations(parcelId);
  return (
    <div className="mt-1 border-l-2 border-emerald-500 pl-2">
      <p className="text-emerald-700 dark:text-emerald-400 font-medium">
        ✓ Rozbieżności uznane za wyjaśnione ({diff.accepted.length})
      </p>
      {diff.accepted.map((r, i) => (
        <div key={i} className="flex flex-wrap items-center gap-1.5">
          <span>
            <b>{r.kw || "—"}</b> (KW) ↔ <b>{r.db || "—"}</b> (działka)
            {r.by && <span className="text-[var(--color-text-muted)]"> · {r.by}</span>}
          </span>
          <button
            className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)] disabled:opacity-40"
            disabled={disabled || m.unacceptDiff.isPending}
            onClick={() => m.unacceptDiff.mutate({ id: fileId, kwName: r.kw, dbName: r.db })}
            title="Cofnij akceptację — rozbieżność wróci na listę"
          >
            cofnij
          </button>
        </div>
      ))}
    </div>
  );
}

function KwFileCard({
  file, parcelId, onTask, disabled,
}: {
  file: ParcelKwFile;
  parcelId: string;
  onTask: (id: string) => void;
  disabled: boolean;
}) {
  const m = useKwMutations(parcelId);
  const a = file.analysis || {};
  const owners = a.owners ?? [];
  const diff = file.owner_diff;
  const warnings = kwWarnings(a.warnings);
  const savedSections = Object.entries(file.sections || {}).filter(([, v]) => (v || "").trim());
  const [showText, setShowText] = useState(false);

  return (
    <div className="border border-[var(--color-border-muted)] bg-[var(--color-bg-elevated)] p-2 text-xs space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono truncate">
          {file.file?.original_filename ?? "KW.pdf"}
          {a.kw_number ? ` · ${a.kw_number}` : ""}
        </span>
        <span className="flex items-center gap-2 shrink-0">
          {file.file?.url && (
            <a href={file.file.url} download className="text-[var(--color-primary)] hover:underline inline-flex items-center gap-1">
              <Download size={12} /> pobierz
            </a>
          )}
          <button
            className="text-[var(--color-primary)] hover:underline inline-flex items-center gap-1"
            disabled={disabled}
            onClick={() => m.analyze.mutate(file.id, { onSuccess: (r) => onTask(r.task_id) })}
          >
            Analizuj ponownie
          </button>
          <button className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]" disabled={disabled}
            onClick={() => m.remove.mutate(file.id)} title="Usuń">
            <Trash2 size={13} />
          </button>
        </span>
      </div>

      {!file.analyzed_at ? (
        <p className="text-[var(--color-text-muted)]">Jeszcze nieanalizowana.</p>
      ) : (
        <>
          {owners.length > 0 && (
            <div>
              <p className="font-medium mb-1">Właściciele wg KW (Dział II) — tylko do wglądu</p>
              <table className="w-full">
                <tbody>
                  {owners.map((o, i) => (
                    <tr key={i} className="border-t border-[var(--color-border-muted)]">
                      <td className="py-0.5">{o.name}{o.role ? ` (${o.role})` : ""}</td>
                      <td className="py-0.5 text-right">{o.share}</td>
                      <td className="py-0.5 text-right font-mono">{o.pesel_nip}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Właścicieli bierzemy z wypisów — z KW tylko sprawdzamy zgodność.
                  Rozbieżność „ten sam podmiot, inny zapis" (GMINA WAGANIEC ↔ Urząd Gminy
                  Waganiec) można ZAAKCEPTOWAĆ — wtedy przestaje się liczyć jako różnica
                  i działka może być zielona na liście. */}
              {diff && (diff.only_in_kw.length > 0 || diff.only_in_db.length > 0) ? (
                <OwnerDiffResolver diff={diff} fileId={file.id} parcelId={parcelId} disabled={disabled} />
              ) : diff ? (
                <p className="mt-1 text-green-600 dark:text-green-400">
                  ✓ Właściciele zgodni z tymi na działce ({diff.matched.length})
                  {diff.accepted.length > 0 && ` + ${diff.accepted.length} zaakceptowanych rozbieżności`}
                </p>
              ) : null}
              {diff && diff.accepted.length > 0 && (
                <AcceptedDiffList diff={diff} fileId={file.id} parcelId={parcelId} disabled={disabled} />
              )}
            </div>
          )}
          {(a.easements ?? []).length > 0 && (
            <div>
              <p className="font-medium mb-0.5">Służebności / ograniczenia (Dział III)</p>
              <ul className="list-disc pl-4 text-amber-700 dark:text-amber-400">
                {(a.easements ?? []).map((e, i) => (
                  <li key={i}>{e.type}{e.beneficiary ? ` — na rzecz: ${e.beneficiary}` : ""}{e.description ? ` (${e.description})` : ""}</li>
                ))}
              </ul>
            </div>
          )}
          {(file.roads ?? []).length > 0 && (
            <div>
              <p className="font-medium mb-0.5">
                Drogi wg księgi
                {(file.roads ?? []).some((r) => r.internal) && (
                  <span className="ml-1.5 text-[9px] uppercase tracking-wide px-1 py-px border border-red-300 bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300">
                    droga wewnętrzna
                  </span>
                )}
              </p>
              <ul className="list-disc pl-4">
                {(file.roads ?? []).map((r, i) => (
                  <li key={i} className={r.internal ? "text-red-700 dark:text-red-300" : undefined}>
                    {r.section ? <span className="font-mono text-[10px] mr-1">[{r.section}]</span> : null}
                    {r.kind}
                    {r.description ? ` — ${r.description}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(a.mortgages ?? []).length > 0 && (
            <div>
              <p className="font-medium mb-0.5">Hipoteki (Dział IV)</p>
              <ul className="list-disc pl-4 text-[var(--color-primary)]">
                {(a.mortgages ?? []).map((h, i) => (
                  <li key={i}>{h.amount} {h.currency} — {h.creditor}{h.type ? ` (${h.type})` : ""}</li>
                ))}
              </ul>
            </div>
          )}
          {warnings.length > 0 && (
            <div>
              <p className="font-medium mb-0.5">Ostrzeżenia</p>
              <ul className="list-disc pl-4 text-[var(--color-text-muted)]">
                {warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}
          <p className="text-[10px] text-[var(--color-text-muted)]">
            Analiza: {file.analyzed_at ? fmtDateTime(file.analyzed_at) : "—"}
            {file.analysis_cost_usd ? ` · ~$${file.analysis_cost_usd}` : ""}
          </p>
        </>
      )}

      {/* PDF-y działów (budowane na serwerze z treści z wtyczki albo wgrane ręcznie) —
          do pobrania pojedynczo, np. do wniosku o wyłączenie z produkcji rolniczej. */}
      {(file.section_files ?? []).length > 0 && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="font-medium">PDF działów:</span>
          <button
            className="text-[var(--color-primary)] hover:underline inline-flex items-center gap-0.5"
            title="Wszystkie działy jako kolejne strony jednego pliku"
            onClick={async () => {
              const resp = await getParcelKwMergedPdf(file.id);
              saveBlobResponse(resp, `KW_${(a.kw_number || "").replace(/\//g, "_") || file.id}.pdf`);
            }}
          >
            <Download size={11} /> cały PDF
          </button>
          <span className="text-[var(--color-text-muted)]">·</span>
          {(file.section_files ?? []).map((s) =>
            s.file?.url ? (
              <a key={s.id} href={s.file.url} download title={s.section_label}
                className="text-[var(--color-primary)] hover:underline inline-flex items-center gap-0.5">
                <Download size={11} />
                {(KW_SECTIONS.find((x) => x.key === s.section)?.label || s.section_label)
                  .replace(/\s*\(.*\)$/, "")}
              </a>
            ) : null)}
        </div>
      )}

      {/* Treść działów zapisana w bazie — wgląd bez wracania na EKW. */}
      {savedSections.length > 0 && (
        <>
          <button className="text-[var(--color-primary)] hover:underline"
            onClick={() => setShowText((s) => !s)}>
            {showText ? "Ukryj" : "Pokaż"} zapisaną treść KW ({savedSections.length} dz.)
          </button>
          {showText && (
            <div className="max-h-64 overflow-auto border border-[var(--color-border-muted)] p-2 space-y-2">
              {savedSections.map(([k, v]) => (
                <div key={k}>
                  <p className="font-medium">{KW_SECTIONS.find((s) => s.key === k)?.label || k}</p>
                  <pre className="whitespace-pre-wrap text-[10px] font-mono">{v}</pre>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
